import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import {
  MdCheckCircleOutline,
  MdHighlightOff,
  MdMoreHoriz,
  MdOutlineAutorenew,
  MdOutlineFactCheck,
  MdOutlineHandshake,
  MdOutlineSave,
  MdOutlineUndo,
} from "react-icons/md";
import { VscChevronDown } from "react-icons/vsc";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import {
  BudgetStatusEnum,
  BudgetSummaryActionOptions,
  BudgetType,
  StatusBudget,
  getBudgetCurrentStatus,
} from "../../../../types/budget/budget";
import { TabActive } from "../../../../types/project.type";
import { formatCurrency } from "../../../../utils/formatters";
import { isSupplierVisibleInVersion } from "../../../../utils/budget-stage-visibility";
import { getVisibleStageGrandTotal } from "../../../../utils/visible-stage-totals";
import {
  hasConsultingBefore,
  TypePill,
  pillStyles,
} from "../ActiveBudget/ActiveBudget";
import {
  useBudgetSave,
  useBudgetSummaryActions,
} from "../hooks/useBudgetSummaryActions";
import { useClosingStageActions } from "../hooks/useClosingStageActions";
import { ExportView, useBudgetExport } from "../hooks/useBudgetExport";

function parseSupplierId(supplierId: string): string {
  if (supplierId.includes("|")) return supplierId.split("|")[0];
  const parts = supplierId.split("-");
  return parts.length > 1 ? parts[0] : supplierId;
}

function countUniqueSuppliers(
  supplierList: { supplierId: string }[] | undefined
): number {
  if (!supplierList?.length) return 0;
  const ids = new Set(supplierList.map((s) => parseSupplierId(s.supplierId)));
  return ids.size;
}

type CountableSupplier = {
  supplierId: string;
  calcOption: string;
  deletedInVersions?: number[];
};

/**
 * Fornecedores únicos exibidos no rodapé. Um fornecedor excluído numa
 * negociação/fechamento continua na `supplierList` de propósito, para não sumir
 * do orçamento original — o contador precisa então usar a MESMA regra da tela
 * (ver `utils/budget-stage-visibility`), senão conta card que ninguém vê.
 *
 * `version < 0` é o comparativo/detalhado, que mostra todas as etapas juntas.
 */
export function countVisibleSuppliers(
  supplierList: CountableSupplier[] | undefined,
  budgetServices:
    | { supplierId?: string; calcOption?: string; versions?: number[] }[]
    | undefined,
  version: number
): number {
  const list = supplierList ?? [];
  if (version < 0) return countUniqueSuppliers(list);
  return countUniqueSuppliers(
    list.filter((el) =>
      isSupplierVisibleInVersion(
        budgetServices,
        el.supplierId,
        el.calcOption,
        version,
        el.deletedInVersions
      )
    )
  );
}

function parseDetailedVersion(detailedBudget: string): number {
  const parts = detailedBudget.split("-");
  const parsed = Number(parts[2]);
  return Number.isFinite(parsed) ? parsed : -1;
}

function getBottomBarVersion(
  detailedBudget: string,
  versionInfo: { version: number },
  tabActive: TabActive
): number {
  const parsed = parseDetailedVersion(detailedBudget);
  if (parsed >= 0) return parsed;
  if (versionInfo?.version >= 0) return versionInfo.version;
  return tabActive === TabActive.BUDGET ? 0 : -1;
}

function getVersionStatus(
  budget: { versionHistory?: { version: number; status: string }[] },
  version: number
): StatusBudget | undefined {
  return budget.versionHistory?.find((v) => v.version === version)?.status as
    | StatusBudget
    | undefined;
}

export function useBudgetBottomBarVisible() {
  const { activeBudget, budgetList, detailedBudget, versionInfo } = useBudget();
  const { tabActive } = useProject();

  if (!budgetList?.length || !activeBudget?._id) return false;

  const activeBudgetIndex = budgetList.findIndex(
    (el) => el._id === activeBudget._id
  );
  if (activeBudgetIndex === -1) return false;

  const version = getBottomBarVersion(detailedBudget, versionInfo, tabActive);
  const versionStatus = getVersionStatus(activeBudget, version);

  switch (tabActive) {
    case TabActive.BUDGET:
      return (
        !!budgetList.find((el) => el.status !== "DELETED") &&
        activeBudget.versionHistory?.[0]?.status === "OPEN"
      );
    case TabActive.NEGOTIATION:
      return (
        version > 0 &&
        !!versionStatus &&
        ["NEGOTIATE", "DENIED"].includes(versionStatus)
      );
    case TabActive.APPROVED:
      return (
        version >= 0 &&
        !!versionStatus &&
        ["APPROVED", "DENIED_CLOSING"].includes(versionStatus)
      );
    default:
      return false;
  }
}

type PrimaryActions = {
  showSave: boolean;
  showNegotiate: boolean;
  showApprove: boolean;
  showDeny: boolean;
};

function getPrimaryActions(tabActive: TabActive): PrimaryActions {
  switch (tabActive) {
    case TabActive.BUDGET:
      return {
        showSave: true,
        showNegotiate: true,
        showApprove: true,
        showDeny: false,
      };
    case TabActive.NEGOTIATION:
      return {
        showSave: true,
        showNegotiate: false,
        showApprove: true,
        showDeny: false,
      };
    case TabActive.APPROVED:
      return {
        showSave: true,
        showNegotiate: false,
        showApprove: false,
        showDeny: true,
      };
    default:
      return {
        showSave: false,
        showNegotiate: false,
        showApprove: false,
        showDeny: false,
      };
  }
}

const BAR_WIDE_LAYOUT_MIN_WIDTH = 1700;

const actionButtonSx = {
  height: { xs: 32, sm: 34, md: 36 },
  minWidth: { xs: 32, sm: 36, md: "auto" },
  borderRadius: { xs: "6px", sm: "8px" },
  fontSize: { xs: 12, sm: 13, md: 14 },
  fontWeight: 400,
  textTransform: "none" as const,
  gap: 1,
  px: { xs: 1, sm: 1.25, md: 2 },
  flexShrink: 0,
  boxShadow: "none",
  whiteSpace: "nowrap" as const,
  "&:hover": { boxShadow: "none" },
  "& .MuiButton-startIcon": {
    mr: { xs: 0, lg: 0.75 },
    ml: { xs: 0, lg: undefined },
  },
  "& .MuiButton-endIcon": {
    ml: { xs: 0, lg: 0.75 },
  },
};

const BudgetBottomBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactActions = useMediaQuery(theme.breakpoints.down("md"));
  const isWideBarLayout = useMediaQuery(
    `(min-width:${BAR_WIDE_LAYOUT_MIN_WIDTH}px)`
  );
  const { tabActive } = useProject();

  const {
    activeBudget,
    budgetList,
    activeBudgetDispatch,
    isBudgetDisabled,
    detailedBudget,
    versionInfo,
    closingControl,
  } = useBudget();
  const { handleSave, isBudgetDisabled: saveDisabled } = useBudgetSave();
  const {
    showClosingStageAction,
    closingStageButton,
    closingStageButtonLabel,
    handleClosingStageAction,
    isClosingControlPending,
  } = useClosingStageActions();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [moreOptionsAnchor, setMoreOptionsAnchor] =
    useState<null | HTMLElement>(null);

  const showBar = useBudgetBottomBarVisible();
  const activeBudgetIndex =
    budgetList?.findIndex((el) => el._id === activeBudget?._id) ?? -1;

  const version = getBottomBarVersion(detailedBudget, versionInfo, tabActive);
  const versionStatus = getVersionStatus(activeBudget, version);
  const versionHasConsulting =
    !!activeBudget?.versionHistory?.[version]?.isConsulting;

  const lastNegVersion = useMemo(() => {
    return (
      activeBudget?.versionHistory
        ?.filter((v) => v.status === "NEGOTIATE" && !v.isConsulting)
        ?.reduce((max, v) => (v.version > max ? v.version : max), 0) ?? 0
    );
  }, [activeBudget?.versionHistory]);

  const isLastNegotiation =
    tabActive === TabActive.NEGOTIATION &&
    version === lastNegVersion &&
    !versionHasConsulting;

  const hasConsultingVersion = useMemo(
    () => activeBudget?.versionHistory?.some((el) => el.isConsulting) ?? false,
    [activeBudget?.versionHistory]
  );

  const { triggerAction, secondaryActions } = useBudgetSummaryActions({
    budget: activeBudget,
    version,
    hasConsultingVersion,
    isLastNegotiation,
  });
  const { exportCurrentBudget, isExporting } = useBudgetExport(version);

  const [isCreatingNegotiation, setIsCreatingNegotiation] = useState(false);

  // Só a última negociação é editável, então só ela pode originar a próxima —
  // mesma regra que antes escondia "Criar nova versão" do menu.
  const canCreateNegotiation =
    tabActive === TabActive.NEGOTIATION &&
    isLastNegotiation &&
    !isBudgetDisabled;

  const handleNewNegotiation = useCallback(async () => {
    if (isCreatingNegotiation) return;
    setIsCreatingNegotiation(true);
    try {
      await triggerAction("NEW_VERSION");
    } finally {
      setIsCreatingNegotiation(false);
    }
  }, [isCreatingNegotiation, triggerAction]);

  const primaryActions = getPrimaryActions(tabActive);

  const inlinePrimaryActions = useMemo(() => {
    if (isMobile) {
      return {
        showSave: primaryActions.showSave,
        showNegotiate: false,
        showApprove: false,
        showDeny: false,
      };
    }
    return primaryActions;
  }, [isMobile, primaryActions]);

  const menuActions = useMemo(() => {
    type MenuAction = {
      key: string;
      label: string;
      onClick: () => void;
      disabled?: boolean;
    };

    const actions: MenuAction[] = secondaryActions.map(
      (option: {
        value: keyof typeof BudgetSummaryActionOptions;
        label: string;
      }) => ({
        key: option.value,
        label: option.label,
        onClick: () => triggerAction(option.value),
      })
    );

    if (isMobile) {
      const primaryInMenu: MenuAction[] = [];
      if (canCreateNegotiation) {
        primaryInMenu.push({
          key: "NEW_VERSION",
          label: "Nova negociação",
          onClick: handleNewNegotiation,
          disabled: isCreatingNegotiation,
        });
      }
      if (primaryActions.showNegotiate) {
        primaryInMenu.push({
          key: "NEGOTIATE",
          label: "Negociar",
          onClick: () => triggerAction("NEGOTIATE"),
          disabled: isBudgetDisabled,
        });
      }
      if (primaryActions.showApprove) {
        primaryInMenu.push({
          key: "APROVE",
          label: "Enviar para fechamento",
          onClick: () => triggerAction("APROVE"),
          disabled: isBudgetDisabled || versionHasConsulting,
        });
      }
      if (primaryActions.showDeny) {
        primaryInMenu.push({
          key: "DENY",
          label: "Reprovar",
          onClick: () => triggerAction("DENY"),
          disabled: isBudgetDisabled,
        });
      }
      return [
        ...primaryInMenu,
        ...actions,
        {
          key: "EXPORT_BY_SUPPLIER",
          label: isExporting ? "Exportando..." : "Exportar por Fornecedor",
          onClick: () => exportCurrentBudget(ExportView.BY_SUPPLIER),
          disabled: isExporting,
        },
        {
          key: "EXPORT_BY_GROUP",
          label: isExporting ? "Exportando..." : "Exportar por Agrupamento",
          onClick: () => exportCurrentBudget(ExportView.BY_GROUP),
          disabled: isExporting,
        },
      ];
    }

    actions.push(
      {
        key: "EXPORT_BY_SUPPLIER",
        label: isExporting ? "Exportando..." : "Exportar por Fornecedor",
        onClick: () => exportCurrentBudget(ExportView.BY_SUPPLIER),
        disabled: isExporting,
      },
      {
        key: "EXPORT_BY_GROUP",
        label: isExporting ? "Exportando..." : "Exportar por Agrupamento",
        onClick: () => exportCurrentBudget(ExportView.BY_GROUP),
        disabled: isExporting,
      }
    );

    return actions;
  }, [
    secondaryActions,
    triggerAction,
    exportCurrentBudget,
    isExporting,
    isMobile,
    primaryActions,
    isBudgetDisabled,
    versionHasConsulting,
    canCreateNegotiation,
    handleNewNegotiation,
    isCreatingNegotiation,
  ]);

  const statusLabel = useMemo(() => {
    if (versionHasConsulting) return BudgetStatusEnum.CONSULTING;

    if (tabActive === TabActive.NEGOTIATION) {
      if (versionStatus === "DENIED") return BudgetStatusEnum.DENIED;

      const shouldDecrementIndex = hasConsultingBefore(
        activeBudget?.versionHistory || [],
        version
      );
      const displayIndex = shouldDecrementIndex ? version - 1 : version;
      return `${BudgetStatusEnum.NEGOTIATE} ${displayIndex}`;
    }

    if (tabActive === TabActive.APPROVED) {
      if (versionStatus === "DENIED_CLOSING") {
        return BudgetStatusEnum.DENIED_CLOSING;
      }
      // Rótulo do fluxo só quando closingStatus está definido na API.
      if (versionStatus === "APPROVED" || !versionStatus) {
        if (closingControl?.closingStatus != null) {
          return closingControl.closingStatusLabel ?? BudgetStatusEnum.APPROVED;
        }
        return BudgetStatusEnum.APPROVED;
      }
      return BudgetStatusEnum[versionStatus as keyof typeof BudgetStatusEnum];
    }

    const statusKey = getBudgetCurrentStatus(activeBudget?.versionHistory);
    return BudgetStatusEnum[statusKey as keyof typeof BudgetStatusEnum];
  }, [
    activeBudget?.versionHistory,
    tabActive,
    version,
    versionHasConsulting,
    versionStatus,
    closingControl?.closingStatus,
    closingControl?.closingStatusLabel,
  ]);

  const statusBadgeColor = useMemo(() => {
    if (versionStatus === "DENIED" || versionStatus === "DENIED_CLOSING") {
      return "#913F3F";
    }
    if (
      tabActive === TabActive.APPROVED &&
      (versionStatus === "APPROVED" || !versionStatus)
    ) {
      if (closingControl?.closingStatus === "PRE_CLOSING") return "#FFA726";
      if (closingControl?.closingStatus === "RETURNED_FOR_ADJUSTMENTS") {
        return "#D09C17";
      }
    }
    return "#1361A4";
  }, [closingControl?.closingStatus, tabActive, versionStatus]);

  const supplierCount = useMemo(
    () =>
      countVisibleSuppliers(
        activeBudget?.supplierList,
        activeBudget?.budgetServices,
        version
      ),
    [activeBudget?.supplierList, activeBudget?.budgetServices, version]
  );

  const serviceItemCount = activeBudget?.budgetServices?.length ?? 0;

  const totalValue = useMemo(() => {
    const status =
      tabActive === TabActive.APPROVED || versionStatus === "APPROVED"
        ? "APPROVED"
        : tabActive === TabActive.NEGOTIATION || versionStatus === "NEGOTIATE"
        ? "NEGOTIATE"
        : "OPEN";
    return getVisibleStageGrandTotal(activeBudget, status, version);
  }, [activeBudget, tabActive, version, versionStatus]);

  const hasNegotiationOrClosing = activeBudget?.versionHistory?.some(
    (v) => v.status === "NEGOTIATE" || v.status === "APPROVED"
  );

  const typeDisabled =
    isBudgetDisabled ||
    !!hasNegotiationOrClosing ||
    tabActive === TabActive.NEGOTIATION ||
    tabActive === TabActive.APPROVED;

  const title = activeBudget?.name || `Orçamento ${activeBudgetIndex + 1}`;

  const handleTypeChange = (budgetType: BudgetType) => {
    activeBudgetDispatch({
      type: "SET_TYPE",
      payload: { type: budgetType },
    });
  };

  const openMoreOptions = Boolean(moreOptionsAnchor);
  const openCompactMenu = Boolean(menuAnchor);

  if (!showBar) return null;

  const renderSecondaryMenu = (
    anchor: HTMLElement | null,
    open: boolean,
    onClose: () => void
  ) => (
    <Menu
      anchorEl={anchor}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      {menuActions.map((option) => (
        <MenuItem
          key={option.key}
          disabled={option.disabled}
          onClick={() => {
            onClose();
            option.onClick();
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  );

  const saveButton = inlinePrimaryActions.showSave ? (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={saveDisabled}
      aria-label="Salvar"
      startIcon={<MdOutlineSave size={isMobile ? 14 : 16} />}
      sx={{
        ...actionButtonSx,
        bgcolor: "#1361A4",
        "&:hover": { bgcolor: "#0f4f85", boxShadow: "none" },
      }}
    >
      <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
        Salvar
      </Box>
    </Button>
  ) : null;

  const isPreClosingDeniedDisabled =
    closingStageButton === "PRE_CLOSING" && versionStatus === "DENIED_CLOSING";

  const closingStageActionButton =
    showClosingStageAction &&
    closingStageButton &&
    closingStageButtonLabel &&
    !isClosingControlPending ? (
      <Button
        variant="contained"
        onClick={handleClosingStageAction}
        disabled={isPreClosingDeniedDisabled}
        aria-label={closingStageButtonLabel}
        startIcon={
          closingStageButton === "PRE_CLOSING" ? (
            <MdOutlineFactCheck size={isMobile ? 14 : 16} />
          ) : (
            <MdOutlineUndo size={isMobile ? 14 : 16} />
          )
        }
        sx={{
          ...actionButtonSx,
          bgcolor: closingStageButton === "PRE_CLOSING" ? "#FFA726" : "#1361A4",
          "&:hover": {
            bgcolor:
              closingStageButton === "PRE_CLOSING" ? "#fb8c00" : "#0f4f85",
            boxShadow: "none",
          },
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
          {closingStageButtonLabel}
        </Box>
      </Button>
    ) : null;

  const newNegotiationButton =
    canCreateNegotiation && !isMobile ? (
      <Button
        variant="outlined"
        onClick={handleNewNegotiation}
        disabled={isCreatingNegotiation}
        aria-label="Nova negociação"
        startIcon={<MdOutlineAutorenew size={isMobile ? 14 : 16} />}
        sx={{
          ...actionButtonSx,
          bgcolor: "#FFF",
          borderColor: "#ED6C02",
          color: "#ED6C02",
          "&:hover": {
            bgcolor: "#FFF4E5",
            borderColor: "#ED6C02",
            boxShadow: "none",
          },
          "&.Mui-disabled": { borderColor: "#F0C89B", color: "#F0C89B" },
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
          Nova negociação
        </Box>
      </Button>
    ) : null;

  const negotiateButton = inlinePrimaryActions.showNegotiate ? (
    <Button
      variant="contained"
      onClick={() => triggerAction("NEGOTIATE")}
      disabled={isBudgetDisabled}
      aria-label="Negociar"
      startIcon={<MdOutlineHandshake size={isMobile ? 14 : 16} />}
      sx={{
        ...actionButtonSx,
        bgcolor: "#D09C17",
        "&:hover": { bgcolor: "#b88914", boxShadow: "none" },
      }}
    >
      <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
        Negociar
      </Box>
    </Button>
  ) : null;

  const approveButton = inlinePrimaryActions.showApprove ? (
    <Button
      variant="contained"
      onClick={() => triggerAction("APROVE")}
      disabled={isBudgetDisabled || versionHasConsulting}
      aria-label="Aprovar"
      startIcon={<MdCheckCircleOutline size={isMobile ? 14 : 16} />}
      sx={{
        ...actionButtonSx,
        bgcolor: "#0D9813",
        "&:hover": { bgcolor: "#0b7f10", boxShadow: "none" },
      }}
    >
      <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
        Aprovar
      </Box>
    </Button>
  ) : null;

  const denyButton = inlinePrimaryActions.showDeny ? (
    <Button
      variant="contained"
      onClick={() => triggerAction("DENY")}
      disabled={isBudgetDisabled}
      aria-label="Reprovar"
      startIcon={<MdHighlightOff size={isMobile ? 14 : 16} />}
      sx={{
        ...actionButtonSx,
        bgcolor: "#913F3F",
        "&:hover": { bgcolor: "#7a3535", boxShadow: "none" },
      }}
    >
      <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
        Reprovar
      </Box>
    </Button>
  ) : null;

  const useIconOnlyMoreMenu = isCompactActions;

  const moreOptionsButton =
    menuActions.length > 0 ? (
      useIconOnlyMoreMenu ? (
        <>
          <Button
            variant="outlined"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label={isMobile ? "Ações" : "Mais opções"}
            sx={{
              ...actionButtonSx,
              bgcolor: "#FFF",
              borderColor: "#E0E0E0",
              color: "#222",
              "&:hover": { bgcolor: "#FAFAFA", borderColor: "#E0E0E0" },
            }}
          >
            <MdMoreHoriz size={isMobile ? 18 : 20} />
          </Button>
          {renderSecondaryMenu(menuAnchor, openCompactMenu, () =>
            setMenuAnchor(null)
          )}
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            onClick={(e) => setMoreOptionsAnchor(e.currentTarget)}
            endIcon={<VscChevronDown size={18} />}
            sx={{
              ...actionButtonSx,
              bgcolor: "#FFF",
              borderColor: "#E0E0E0",
              color: "#222",
              "&:hover": { bgcolor: "#FAFAFA", borderColor: "#E0E0E0" },
            }}
          >
            Mais opções
          </Button>
          {renderSecondaryMenu(moreOptionsAnchor, openMoreOptions, () =>
            setMoreOptionsAnchor(null)
          )}
        </>
      )
    ) : null;

  const titleRow = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        flexShrink: 1,
      }}
    >
      <Typography
        noWrap
        sx={{
          fontSize: { xs: 13, sm: 14, md: 16 },
          fontWeight: 600,
          color: "#222",
          minWidth: 0,
          maxWidth: { xs: 160, sm: 240, md: "none" },
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          bgcolor: statusBadgeColor,
          color: "#FFF",
          borderRadius: "4px",
          px: { xs: 0.75, sm: 1 },
          py: { xs: 0.25, sm: 0.5 },
          fontSize: { xs: 11, sm: 12, md: 14 },
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {statusLabel}
      </Box>
    </Box>
  );

  const typeRow = (alwaysVisible = false) => (
    <Box
      sx={{
        display: alwaysVisible ? "flex" : { xs: "none", sm: "flex" },
        alignItems: "center",
        gap: 1,
        flexWrap: "nowrap",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: { sm: 12, md: 14 },
          color: "#555",
          whiteSpace: "nowrap",
          flexShrink: 0,
          display: { sm: "none", md: "block" },
        }}
      >
        Tipo:
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "nowrap",
          flexShrink: 0,
          "& > span": {
            height: { xs: 22, sm: 24 },
            padding: { xs: "0 8px", sm: "0 10px" },
            fontSize: { xs: 11, sm: 12 },
            borderRadius: 12,
          },
        }}
      >
        {activeBudget.type ? (
          /**
           * O tipo passou a ser definido na criação do orçamento, então aqui
           * ele é só informação — mostrar o tipo oposto como pill clicável dava
           * a entender que Logística e Produção eram visões do mesmo orçamento.
           */
          <TypePill
            label={
              activeBudget.type === BudgetType.LOGISTICA
                ? "Logística"
                : "Produção"
            }
            active
            activeStyle={
              activeBudget.type === BudgetType.LOGISTICA
                ? pillStyles.activeLog
                : pillStyles.activePro
            }
            disabled
            onClick={() => {}}
          />
        ) : (
          /**
           * Orçamentos criados antes dessa mudança ficaram sem `type` e o envio
           * para negociação/fechamento o exige — sem o toggle aqui não haveria
           * como defini-lo.
           */
          <>
            <TypePill
              label="Logística"
              active={false}
              activeStyle={pillStyles.activeLog}
              disabled={typeDisabled}
              onClick={() => handleTypeChange(BudgetType.LOGISTICA)}
            />
            <TypePill
              label="Produção"
              active={false}
              activeStyle={pillStyles.activePro}
              disabled={typeDisabled}
              onClick={() => handleTypeChange(BudgetType.PRODUCAO)}
            />
          </>
        )}
      </Box>
    </Box>
  );

  const metricsNodes = (
    <>
      <Metric
        label="Fornecedores"
        value={String(supplierCount)}
        compact
        size={isCompactActions ? "sm" : "md"}
      />
      <MetricDivider />
      <Metric
        label={isCompactActions ? "Itens" : "Itens de serviço"}
        value={String(serviceItemCount)}
        compact
        size={isCompactActions ? "sm" : "md"}
      />
      <MetricDivider />
      <Metric
        label="Valor total"
        value={formatCurrency(totalValue)}
        size={isCompactActions ? "sm" : "md"}
      />
    </>
  );

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        flexShrink: 0,
        width: "100%",
        boxSizing: "border-box",
        bgcolor: "#FBFCFF",
        borderTop: "1px solid #F0F0F0",
        boxShadow: "0px -2px 5px rgba(0, 0, 0, 0.05)",
        zIndex: (theme) => theme.zIndex.appBar,
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 0.75, sm: 1, md: 1.25 },
        overflowX: "auto",
        overflowY: "visible",
        display: "grid",
        gridTemplateColumns: isWideBarLayout
          ? "minmax(0, auto) minmax(0, 1fr) max-content"
          : "1fr",
        gridTemplateAreas: isWideBarLayout
          ? `"identity metrics actions"`
          : `"info" "actions"`,
        alignItems: "center",
        columnGap: isWideBarLayout ? 2 : 1.5,
        rowGap: { xs: 0.75, sm: 1 },
        minHeight: isWideBarLayout ? 58 : 52,
      }}
    >
      {!isWideBarLayout ? (
        <Box
          sx={{
            gridArea: "info",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: { xs: 0.75, sm: 1, md: 1.5 },
            minWidth: 0,
            width: "100%",
          }}
        >
          {titleRow}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: { xs: 20, sm: 28 },
              display: { xs: "none", sm: "block" },
              flexShrink: 0,
            }}
          />
          {typeRow(true)}
          <MetricDivider />
          {metricsNodes}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              gridArea: "identity",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            {titleRow}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 32, alignSelf: "center", flexShrink: 0 }}
            />
            {typeRow()}
          </Box>

          <Box
            sx={{
              gridArea: "metrics",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "nowrap",
              gap: { xs: 0.75, sm: 1, md: 1.5 },
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {metricsNodes}
          </Box>
        </>
      )}

      <Box
        sx={{
          gridArea: "actions",
          display: "flex",
          alignItems: "center",
          justifyContent: isWideBarLayout ? "flex-end" : "center",
          gap: { xs: 0.75, sm: 1 },
          width: "100%",
          flexShrink: 0,
          minWidth: "max-content",
        }}
      >
        {saveButton}
        {newNegotiationButton}
        {closingStageActionButton}
        {negotiateButton}
        {approveButton}
        {denyButton}
        {moreOptionsButton}
      </Box>
    </Box>
  );
};

function MetricDivider() {
  return (
    <Divider
      orientation="vertical"
      flexItem
      sx={{
        height: { xs: 20, sm: 28 },
        display: { xs: "none", sm: "block" },
        flexShrink: 0,
      }}
    />
  );
}

function Metric({
  label,
  value,
  compact = false,
  size = "md",
}: {
  label: string;
  value: string;
  compact?: boolean;
  size?: "sm" | "md";
}) {
  const fontSize =
    size === "sm" ? { xs: 11, sm: 12 } : { xs: 12, sm: 13, md: 14 };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexShrink: compact ? 0 : undefined,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize,
          color: "#666",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        noWrap
        sx={{
          fontSize,
          fontWeight: 600,
          color: "#222",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default BudgetBottomBar;
